import type videojs from 'video.js';
import { VideoEnvironment } from '../../../common/_types/environment';
import {
	AvailableVideoFiles,
	VideoMetaRequestMode,
	VideoMetaResponse,
} from '../video/video-player/video-player.interface';
import axios from 'axios';
import jsonp from 'jsonp';
import { dsConsole } from './console';

/**
 * Fetch meta data for given videoId from Cloudfront.
 * The endpoint returns a JSONP response, with the callback function name in the server response, making it harder to
 * encapsulate each request when there are multiple.
 * Using string replacement on the response, the endpoint can be used as if it was JSON, however only if the domain is included
 * in the CORS whitelist (so it won't work on local)
 *
 * This function supports both JSON and JSONP, as well as multiplme environments.
 */
export const fetchMetaData = async (
	videoId: string,
	environment: VideoEnvironment,
	metaRequestMode: VideoMetaRequestMode = 'json'
): Promise<VideoMetaResponse> => {
	const videoMetaUrl = getUrlForEnv(environment) + 'meta/' + videoId + '.txt';

	return new Promise<VideoMetaResponse>((resolve, reject) => {
		if (metaRequestMode === 'jsonp') {
			const jsonPConf = {
				name: 'videoMeta', // The function name as contained in the server response
				timeout: 2000,
			};

			jsonp(videoMetaUrl, jsonPConf, (err, videoMeta: VideoMetaResponse) => {
				if (err) {
					reject(err);
				} else {
					resolve(videoMeta);
				}
			});
		} else {
			// Use Axios / regular AJAX request, and convert the JSONP response to JSON
			axios
				.get(videoMetaUrl)
				.then((response: { data: string }) => {
					const processedResponse = convertJSONPtoJSON(response.data);
					try {
						const videoMeta: VideoMetaResponse = JSON.parse(processedResponse);
						resolve(videoMeta);
					} catch (e) {
						reject(e);
						dsConsole.error(e);
					}
				})
				.catch((error) => reject(error));
		}
	});
};

/**
 * Converts JSONP string server response to JSON, allowing it to be parsed into an object
 * @see fetchMetaData()
 */
export const convertJSONPtoJSON = (response: string) => {
	if (response.substr(0, 10) === 'videoMeta(') {
		const responseLeftTruncated = response.substr(10);
		return responseLeftTruncated.substr(0, responseLeftTruncated.length - 1);
	}
	return response;
};

/**
 * Maps the different versions (resolutions) of the video from a complex array structure to a simple object,
 * eg. {'720p': '20200407_Nefouse_MB-531.720p.wide.mp4'}
 */
export const mapAvailableVideoFiles = (
	videoMeta: VideoMetaResponse
): AvailableVideoFiles => {
	const availableVideoUrls = {};
	videoMeta.streams.forEach(
		(stream) => (availableVideoUrls[stream[0]] = stream[1])
	);
	return availableVideoUrls;
};

/**
 * Generate a video URL from the Cloudfront metadata response. Defaults to 720p.
 */
export const generateVideoUrl = function (
	videoMeta: VideoMetaResponse,
	resolution = null
): string {
	const availableVideos = mapAvailableVideoFiles(videoMeta);

	if (!resolution) {
		resolution = '720p' in availableVideos ? '720p' : '480p';
	}

	return resolution in availableVideos
		? videoMeta.streamurl + availableVideos[resolution]
		: null;
};

/**
 * Note:	The config array created by this function results in videoJs making an HTTP request to whatever
 * 				the track's src is set to. Due to CORS issues, these requests will fail unless the page is on a real CWP
 * 				test/prod domain. To make these requests work locally you have to have the proxy server running.
 *			 	Therefore, if `isLocal` is true, then it will attempt to use the local proxy server for loading the files
 */
export const getTextTracks = (
	videoMeta: VideoMetaResponse,
	environment: VideoEnvironment
): videojs.TextTrackOptions[] => {
	const isLocal = environment === 'local';

	return generateCaptions(videoMeta, isLocal).concat(
		generateDescriptions(videoMeta, isLocal)
	);
};

/**
 * Generate a list of 'captions' track from cloudfront video metadata to pass to video.js
 */
export const generateCaptions = (
	videoMeta: VideoMetaResponse,
	isLocal = false
): videojs.TextTrackOptions[] => {
	const textTracks = [];
	if (videoMeta.captions?.length) {
		videoMeta.captions.forEach((caption) => {
			const fullLanguage = caption[0];
			const languageCode = caption[1];
			const filename = caption[2];

			textTracks.push({
				src:
					(isLocal
						? getUrlForEnv('local') + 'captions/'
						: videoMeta.captionsUrl) + filename,
				kind: 'captions',
				srclang: languageCode,
				label: fullLanguage,
			});
		});
	}
	return textTracks;
};

/**
 * Generate a list of 'descriptions' tracks from cloudfront video metadata to pass to video.js
 */
export const generateDescriptions = (
	videoMeta: VideoMetaResponse,
	isLocal = false
): videojs.TextTrackOptions[] => {
	const textTracks = [];
	if (window.speechSynthesis && videoMeta.audioDescriptions?.length) {
		videoMeta.audioDescriptions.forEach((audioDescription) => {
			const fullLanguage = audioDescription[0];
			const languageCode = audioDescription[1];
			const filename = audioDescription[2];

			textTracks.push({
				src:
					(isLocal
						? getUrlForEnv('local') + 'audio-descriptions/'
						: videoMeta.audioDescriptionsUrl) + filename,
				kind: 'descriptions',
				srclang: languageCode,
				label: fullLanguage,
			});
		});
	}
	return textTracks;
};

export const getUrlForEnv = (env: VideoEnvironment) => {
	switch (env) {
		// The local environment is the best way to test the video player locally (while developing) but it does require a
		// proxy server to be running. This can be started with `npm run proxy:cloudfront`. This will proxy through to the
		// test environment below for both metadata requests and captions or audio description requests.
		// JSON requests in Storybook can also be made this way, which emulates the prod behaviour
		case 'local':
			return 'http://localhost:8765/';

		// The test environment will load video metadata from the cloudfront test URL. Production video ids will not work
		// unless they are also uploaded to the test environment.
		// Also, due to CORS issues, subtitles and audio descriptions can only be loaded using this environment if:
		//  - The page hostname is a real CWP environment
		//  - OR we use JSONP instead of JSON (which isn't so good for loading multiple videos)
		case 'test':
			return 'https://d3eevne2u0akly.cloudfront.net/';

		// Prod environment. Similar to test environment. Some videos in prod environment may not be in test, and vice-versa.
		case 'prod':
			return 'https://d2adqanfijfsj5.cloudfront.net/';
	}
};

export const validateYoutubeVideoId = (videoId: string) => {
	if (videoId.length !== 11) {
		dsConsole.error(
			`Youtube video ID validation failed for id: ${videoId}. It must be exactly 11 characters.`
		);
	}
};

/**
 * @description
 * This function checks whether videos are allowed to drop marketing cookies
 *
 * @returns true if analytics cookies are enabled
 */
export const areMarketingCookiesAllowed = () => {
	return (
		window.globalAnalyticsParams &&
		window.globalAnalyticsParams.analyticsAllowed == 'true'
	);
};
